// src/components/AdminCentral/Teachers/MassUncertifyModal.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
`;

const Dialog = styled.div`
  margin: 0;
  font-family: Verdana,Arial,sans-serif;
  font-size: 1.1em;
  border: 1px solid #aaaaaa;
  background: #ffffff;
  color: #222222;
  border-radius: 4px;
  padding: .2em;
  width: 450px;
  outline: none;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eeeeee;
  padding: .4em .6em;
  border-bottom: 1px solid #cccccc;
  .title { font-weight: bold; }
  .close {
    background: none; border: none; cursor: pointer;
    font-size: 1.2em; line-height: 1;
  }
`;

const Content = styled.div`
  padding: 1em .5em;
  font-size: 0.95em;
  line-height: 1.4;
  & > div { margin-left: 15px; }
  .counts b { font-size: 1.2em; }
  .disclaimer {
    border: 1px solid #000;
    padding: 8px;
    font-size: 0.8em;
    text-align: justify;
    margin: .8em 1em;
    background: #fafafa;
  }
`;

const ButtonPane = styled.div`
  padding: .5em;
  text-align: right;
  button {
    margin-left: .5em;
    padding: .4em .8em;
    font-size: 0.9em;
    border: 1px solid #888;
    background: #f0f0f0;
    border-radius: 3px;
    cursor: pointer;
    &:hover { background: #e0e0e0; }
  }
`;

export default function MassUncertifyModal({
  show,
  invoiceId,
  teachers = [],
  onCancel,
  onConfirm
}) {
  const [counts, setCounts]       = useState({ schools:0, teachers:0, implementations:0 });
  const [showResult, setShowResult] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [resultStatus, setResultStatus] = useState("Success");

  // when opened, fetch how many are currently certified
  useEffect(() => {
    if (!show) return;
    setShowResult(false);
    API.confirmAllUncertification({ invoice_id: invoiceId })
      .then(resp => {
        const grades = resp.grades || [];
        const implIds    = new Set(grades?.map(g => g.implementation_id));
        const teacherIds = new Set();
        const schoolIds  = new Set();
        implIds.forEach(id => {
          const t = teachers?.find(t => t.implementation_id === id);
          if (t) {
            teacherIds.add(t.customer_user_id);
            if (t.school_id) schoolIds.add(t.school_id);
          }
        });
        setCounts({
          implementations: implIds.size,
          teachers:       teacherIds.size,
          schools:        schoolIds.size,
        });
      })
      .catch(err => {
        console.error("Failed to fetch uncertify info:", err);
        setCounts({ schools:0, teachers:0, implementations:0 });
      });
  }, [show, invoiceId, teachers]);

  // user clicks OK to proceed with batch-uncertify
  const handleOk = () => {
    API.uncertifyAllImplementation({ invoice_id: invoiceId })
      .then(resp => {
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setResultMsg(resp.message || (status === "Failed" ? "Uncertification failed." : "Uncertification complete."));
        setResultStatus(status);
        setShowResult(true);
      })
      .catch(err => {
        console.error("Failed to uncertify all:", err);
        setResultMsg(err.message || "Uncertification failed.");
        setResultStatus("Failed");
        setShowResult(true);
      });
  };

  // final modal dismissal
  const handleFinalClose = () => {
    setResultStatus("Success");
    onConfirm && onConfirm();
  };

  if (!show) return null;

  return (
    <>
      {/* counts dialog */}
      {!showResult && (
        <Overlay>
          <Dialog role="dialog" aria-labelledby="mass_uncertify_title">
            <TitleBar>
              <span className="title" id="mass_uncertify_title">
                Uncertify All Implementations
              </span>
              <button className="close" onClick={onCancel} aria-label="Close">×</button>
            </TitleBar>
            <Content>
              <div className="counts">
                <b>{counts.schools}</b> school(s),<br/>
                <b>{counts.teachers}</b> teacher(s),<br/>
                <b>{counts.implementations}</b> implementation(s) will be affected.
              </div>
              <div className="disclaimer">
                By clicking "OK" I confirm that I wish to remove the certified status from these educator implementation records. This action will appear in compliance reports and may affect audit documentation.
              </div>
              <div style={{ marginLeft: 15 }}>Proceed?</div>
            </Content>
            <ButtonPane>
              <button onClick={onCancel}>Cancel</button>
              <button onClick={handleOk}>OK</button>
            </ButtonPane>
          </Dialog>
        </Overlay>
      )}

      {/* final status dialog */}
      <ConfirmationModal
        show={showResult}
        title="Uncertification Status"
        message={resultMsg}
        onCancel={handleFinalClose}
        onConfirm={handleFinalClose}
        status={resultStatus}
        single
      />
    </>
  );
}
